import apiClient from './client'

const OBJECT_URL_LIFETIME_MS = 30_000
export const DOWNLOAD_ERROR_EVENT = 'domusaurea:download-error'

function notifyDownloadError(message: string) {
  window.dispatchEvent(new CustomEvent(DOWNLOAD_ERROR_EVENT, { detail: { message } }))
}

function normalizeFilename(filename: string) {
  const normalized = filename
    .normalize('NFKC')
    .replace(/[\\/:*?"<>|\u0000-\u001f]/g, '_')
    .trim()
  return normalized || 'download'
}

function clickDownloadLink(url: string, filename: string) {
  const link = document.createElement('a')
  link.href = url
  link.download = normalizeFilename(filename)
  link.rel = 'noopener'
  link.style.display = 'none'
  document.body.appendChild(link)
  try {
    link.click()
  } finally {
    link.remove()
  }
}

function createShareFile(filename: string, blob: Blob) {
  const type = blob.type || (filename.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream')
  return new File([blob], normalizeFilename(filename), { type })
}

/** La condivisione nativa è il percorso di salvataggio mobile; su desktop si scarica direttamente. */
export function shouldUseNativeShare() {
  const userAgent = navigator.userAgent || ''
  return /iPhone|iPad|iPod|Android/i.test(userAgent)
    || (/Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1) // iPadOS con "Sito desktop"
}

/** Indica se il browser può aprire la condivisione nativa per questo file. */
export function canShareFile(filename: string, blob: Blob) {
  try {
    const file = createShareFile(filename, blob)
    return typeof navigator.share === 'function'
      && typeof navigator.canShare === 'function'
      && navigator.canShare({ files: [file] })
  } catch {
    return false
  }
}

/** Apre il pannello nativo iOS/Android per condividere o salvare il file. */
export async function shareFile(filename: string, blob: Blob) {
  if (!canShareFile(filename, blob)) return false
  const file = createShareFile(filename, blob)
  await navigator.share({ files: [file], title: file.name })
  return true
}

/** Avvia il download da un URL locale già creato, per esempio una preview PDF. */
export function downloadUrl(url: string, filename: string) {
  clickDownloadLink(url, filename)
}

/** Avvia il download di un Blob già in memoria usando il percorso standard del browser. */
export function downloadBlob(filename: string, blob: Blob) {
  if (!(blob instanceof Blob)) throw new TypeError('Il contenuto da scaricare non è un file valido.')

  let objectUrl: string | null = null
  try {
    objectUrl = URL.createObjectURL(blob)
    clickDownloadLink(objectUrl, filename)
  } catch (error) {
    notifyDownloadError(error instanceof Error ? error.message : 'Non è stato possibile avviare il download.')
    throw error
  } finally {
    // Safari and mobile browsers can start reading a Blob URL after click() returns.
    if (objectUrl) window.setTimeout(() => URL.revokeObjectURL(objectUrl!), OBJECT_URL_LIFETIME_MS)
  }
}

/**
 * Scarica un file da un endpoint autenticato tramite Blob, così il token Bearer
 * viaggia con la richiesta (a differenza di window.open/href diretti verso [Authorize]).
 */
export async function downloadFile(
  url: string,
  filename: string,
  params?: Record<string, unknown>,
  options: { notifyError?: boolean } = {},
) {
  try {
    const res = await apiClient.get(url, { params, responseType: 'blob' })
    const blob = res.data as Blob
    if (!(blob instanceof Blob) || blob.size === 0) {
      throw new Error('Il server ha restituito un file vuoto.')
    }
      if (blob.type.toLowerCase().includes('pdf') && shouldUseNativeShare()) {
      try {
        if (await shareFile(filename, blob)) return
      } catch (shareError) {
        // Annullare il pannello è una scelta dell'utente: non avviare un download a sorpresa.
        if (shareError instanceof DOMException && shareError.name === 'AbortError') return
        // Se il browser rifiuta la condivisione (per esempio perché è scaduto il gesto utente),
        // il download classico resta disponibile come fallback.
      }
    }
    downloadBlob(filename, blob)
  } catch (error) {
    const message = await readDownloadError(error)
    if (options.notifyError !== false) notifyDownloadError(message)
    throw new Error(message, { cause: error })
  }
}

async function readDownloadError(error: unknown): Promise<string> {
  const responseData = (error as { response?: { data?: unknown } })?.response?.data
  if (responseData instanceof Blob) {
    const text = await responseData.text().catch(() => '')
    if (text) {
      try {
        const body = JSON.parse(text) as { message?: string; error?: string; title?: string; errors?: string[] | Record<string, string[]> }
        if (body.message || body.error || body.title) return body.message ?? body.error ?? body.title!
        if (Array.isArray(body.errors) && body.errors.length) return body.errors.join(' ')
        if (body.errors && typeof body.errors === 'object') {
          const errors = Object.values(body.errors).flat()
          if (errors.length) return errors.join(' ')
        }
      } catch {
        // Le risposte binarie d'errore possono essere testo semplice.
        if (text.length < 300) return text
      }
    }
  }
  const status = (error as { response?: { status?: number } })?.response?.status
  if (status) return `Il server non ha completato il download (errore ${status}).`
  if (error instanceof Error && error.message) return error.message
  return 'Non è stato possibile scaricare il file. Riprova.'
}
